/**
 * 设备通信协议处理模块
 * 包含设备 HID 管理、命令发送、响应处理
 * 对齐旧版 js/server-1.js 的通信逻辑
 * 
 * 导入类型和协议处理函数
 */
import type { DeviceInfo, DeviceDetail, DeviceStatus, ConnectionType } from './types'
import {
  buildCommand,
  buildGetStatusCommand,
  buildGetDeviceDetailCommand,
  parseHeadsetResponse,
  parseDeviceDetailResponse,
  bytesToHexString,
} from './protocol'

/**
 * HID 事件类型映射
 * 定义所有可能的事件及其数据类型
 */
// Event Types
export interface HIDDeviceEventMap {
  'device-connected': DeviceDetail
  'device-disconnected': void
  'status-updated': DeviceStatus
  'data-received': Uint8Array
  'error': Error
  'connection-stage': {
    stage:
      | 'idle'
      | 'authorized_found'
      | 'requesting_authorization'
      | 'opening_device'
      | 'waiting_for_pairing'
      | 'connected'
      | 'disconnected'
      | 'upgrade_mode'
    message?: string
    connectionType?: ConnectionType
  }
}

type EventCallback<T extends keyof HIDDeviceEventMap> = (data: HIDDeviceEventMap[T]) => void

type StoredDeviceState = {
  isConnected?: boolean
  isDeviceConnected?: boolean
  connectionType?: ConnectionType
  deviceInfo?: {
    productId: number
    vendorId: number
    productName: string
  }
}

const STORAGE_KEY = 'deviceConnectionState'

function loadStoredState(): StoredDeviceState {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') as StoredDeviceState
  } catch {
    return {}
  }
}

function saveStoredState(state: StoredDeviceState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // ignore
  }
}

function clearStoredState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

export function isWebHIDSupported(): boolean {
  return typeof navigator !== 'undefined' && 'hid' in navigator && !!navigator.hid
}

export function getWebHIDSupportError(): string | null {
  if (typeof navigator === 'undefined') return '浏览器环境不支持'
  if (!('hid' in navigator)) return '当前浏览器不支持 WebHID API，请使用 Chrome/Edge 浏览器'
  if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
    return 'WebHID 需要 HTTPS 安全连接'
  }
  return null
}

/**
 * WebHID 设备管理器类
 * 
 * 实现功能:
 * - 记住上次授权的设备 (localStorage)
 * - 启动时自动连接授权设备，无需权限提示
 * - 根据状态标志位等待真正的配对成功
 * - 设备插拔时触发 UI 阶段事件和状态清理
 */
export class HIDDeviceManager {
  private device: HIDDevice | null = null
  private listeners: Map<keyof HIDDeviceEventMap, Set<EventCallback<any>>> = new Map()
  private inputReportHandler: ((e: HIDInputReportEvent) => void) | null = null
  private externalDisconnectHandler: ((e: HIDConnectionEvent) => void) | null = null
  private externalConnectHandler: ((e: HIDConnectionEvent) => void) | null = null

  private isAutoConnecting = false
  private isDeviceConnected = false // pc_isConnected equivalent
  private lastConnectedDeviceKey: string | null = null // track most-recently plugged device

  // Single listener architecture - state machine for command/response matching
  private commandResolver: ((value: Uint8Array) => void) | null = null
  private commandRejecter: ((reason: Error) => void) | null = null
  private commandTimeout: ReturnType<typeof setTimeout> | null = null
  private isWaitingForResponse = false

  // Vendor/Product IDs from original project
  private readonly DEVICE_FILTERS = [
    { vendorId: 0x1235, productId: 0x0043 },
    { vendorId: 0x2235, productId: 0x0059 },
    { vendorId: 0x1235, productId: 0x0057 },
    { vendorId: 0x1235, productId: 0x0067 },
    { vendorId: 0x1235, productId: 0x0059 },
  ]

  private emit<T extends keyof HIDDeviceEventMap>(event: T, data: HIDDeviceEventMap[T]): void {
    const callbacks = this.listeners.get(event)
    if (callbacks) {
      callbacks.forEach(cb => {
        try {
          cb(data)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Event handler error:', error)
        }
      })
    }
  }

  on<T extends keyof HIDDeviceEventMap>(event: T, callback: EventCallback<T>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(callback as EventCallback<any>)

    return () => {
      this.listeners.get(event)?.delete(callback as EventCallback<any>)
    }
  }

  getConnectionType(): ConnectionType {
    if (!this.device) return null
    // Original mapping:
    // 2.4G: pid 67
    // USB: pid 89
    if (this.device.productId === 67) return '2.4G'
    if (this.device.productId === 89) return 'USB'

    // Keep compatibility with existing filter list (hex values)
    if (this.device.productId === 0x0043 || this.device.productId === 0x0057) return '2.4G'
    if (this.device.productId === 0x0059 || this.device.productId === 0x0067) return 'USB'

    return null
  }

  private checkDeviceConnectionStatus(status: DeviceStatus): boolean {
    const connectionType = this.getConnectionType()

    if (connectionType === '2.4G') {
      return status.headsetConnected === 1
    }

    if (connectionType === 'USB') {
      return status.linkStatus === 1
    }

    return false
  }

  private hasReport(device: HIDDevice, reportId: number): boolean {
    return device.collections.some(col => {
      const inputReports = col.inputReports || []
      const outputReports = col.outputReports || []
      return inputReports.some(r => r.reportId === reportId) || outputReports.some(r => r.reportId === reportId)
    })
  }

  private hasNormalModeReport49(device: HIDDevice): boolean {
    return device.collections.some(col => {
      const inputReports = col.inputReports || []
      const outputReports = col.outputReports || []
      const hasInput49 = inputReports.some(r => r.reportId === 49)
      const hasOutput49 = outputReports.some(r => r.reportId === 49)
      return hasInput49 && hasOutput49
    })
  }

  private hasUpgradeModeReport0(device: HIDDevice): boolean {
    return device.collections.some(col => {
      const inputReports = col.inputReports || []
      const outputReports = col.outputReports || []
      const hasInput0 = inputReports.some(r => r.reportId === 0)
      const hasOutput0 = outputReports.some(r => r.reportId === 0)
      return hasInput0 && hasOutput0
    })
  }

  /**
   * 获取已授权的设备列表
   * 只返回支持报告 ID 49 的设备（正常模式）
   * 
   * @returns 已授权的 HID 设备数组
   */
  async getAuthorizedDevices(): Promise<HIDDevice[]> {
    if (!navigator.hid) return []

    const devices = await navigator.hid.getDevices()
    // Original project: “业务匹配” = reportId=49
    return devices.filter(d => this.hasReport(d, 49))
  }

  async hasAuthorizedDevices(): Promise<boolean> {
    const devices = await this.getAuthorizedDevices()
    return devices.length > 0
  }

  /**
   * 初始化 HID 管理器
   * 在应用启动时调用，设置全局监听器并尝试自动连接
   * 
   * @returns 是否成功初始化（true=初始化成功且已尝试自动连接）
   */
  async init(): Promise<boolean> {
    if (!isWebHIDSupported()) {
      const msg = getWebHIDSupportError() || 'WebHID API not supported'
      this.emit('error', new Error(msg))
      return false
    }

    this.setupGlobalHIDListeners()
    return this.autoConnect()
  }

  /**
   * 自动连接（无权限提示）
   * 连接到已授权的设备，使用 navigator.hid.getDevices() 获取
   * 
   * @returns 连接是否成功
   */
  async autoConnect(): Promise<boolean> {
    if (this.isAutoConnecting || this.device) return false
    if (!navigator.hid) {
      this.emit('error', new Error('WebHID API not supported'))
      return false
    }

    this.isAutoConnecting = true

    try {
      const allDevices = await navigator.hid.getDevices()

      const hasUpgrade = allDevices.some(d => this.hasUpgradeModeReport0(d))
      const hasNormal = allDevices.some(d => this.hasNormalModeReport49(d))
      if (hasUpgrade && !hasNormal) {
        this.emit('connection-stage', { stage: 'upgrade_mode', message: '检测到升级模式设备' })
        this.isAutoConnecting = false
        return false
      }

      const devices = await this.getAuthorizedDevices()
      if (devices.length === 0) {
        this.isAutoConnecting = false
        return false
      }

      this.emit('connection-stage', { stage: 'authorized_found' })

      // 优先取最新插入的设备（lastConnectedDeviceKey），其次取已保存的设备，最后取第一个
      let target: HIDDevice = devices[0]
      if (this.lastConnectedDeviceKey) {
        const [vid, pid] = this.lastConnectedDeviceKey.split(':').map(Number)
        target = devices.find(d => d.vendorId === vid && d.productId === pid) || target
      } else {
        const saved = loadStoredState()
        if (saved.deviceInfo) {
          target = devices.find(d => d.productId === saved.deviceInfo!.productId && d.vendorId === saved.deviceInfo!.vendorId) || target
        }
      }

      const ok = await this.connectToDevice(target)
      this.isAutoConnecting = false
      return ok
    } catch (error) {
      this.emit('error', error instanceof Error ? error : new Error(String(error)))
      this.isAutoConnecting = false
      return false
    }
  }

  /**
   * 手动连接（显示权限提示窗口）
   * 用户手动点击连接时调用，会弹出系统权限提示
   * 
   * @returns 连接是否成功
   */
  async connect(): Promise<boolean> {
    if (!isWebHIDSupported() || !navigator.hid) {
      const msg = getWebHIDSupportError() || 'WebHID API not supported'
      this.emit('error', new Error(msg))
      return false
    }

    this.emit('connection-stage', { stage: 'requesting_authorization' })

    try {
      const devices = await navigator.hid.requestDevice({ filters: this.DEVICE_FILTERS })
      if (devices.length === 0) return false

      // Prefer normal mode reportId=49 device; if only upgrade mode -> stage
      let normal: HIDDevice | null = null
      let upgrade: HIDDevice | null = null

      for (const dev of devices) {
        if (this.hasNormalModeReport49(dev)) normal = dev
        if (this.hasUpgradeModeReport0(dev)) upgrade = dev
      }

      if (normal) {
        return await this.connectToDevice(normal)
      }

      if (upgrade) {
        this.emit('connection-stage', { stage: 'upgrade_mode', message: '设备处于升级模式' })
        return false
      }

      return false
    } catch (error) {
      this.emit('error', error instanceof Error ? error : new Error(String(error)))
      return false
    }
  }

  private async connectToDevice(targetDevice: HIDDevice): Promise<boolean> {
    console.log('[HID] Connecting to device:', targetDevice.productName, 'VID:', targetDevice.vendorId, 'PID:', targetDevice.productId)
    this.emit('connection-stage', { stage: 'opening_device' })

    this.device = targetDevice
    this.isDeviceConnected = false

    try {
      if (!this.device.opened) {
        console.log('[HID] Opening device...')
        await this.device.open()
        console.log('[HID] Device opened successfully')
      } else {
        console.log('[HID] Device already open')
      }

      const connectionType = this.getConnectionType()

      // Save state to localStorage (authorized device memory)
      saveStoredState({
        isConnected: true,
        isDeviceConnected: false,
        connectionType,
        deviceInfo: {
          productId: this.device.productId,
          vendorId: this.device.vendorId,
          productName: this.device.productName,
        },
      })

      // 先挂 inputreport 监听，再发首包；否则可能错过第一帧响应
      this.setupEventListeners()

      // 这里只发阶段事件，不应该强制驱动独立等待页面
      if (connectionType === '2.4G') {
        this.emit('connection-stage', { stage: 'waiting_for_pairing', message: '等待耳机配对', connectionType })
      } else if (connectionType === 'USB') {
        this.emit('connection-stage', { stage: 'waiting_for_pairing', message: '等待USB连接', connectionType })
      }

      // Fetch device status and detail (like original project fetchDeviceStatus)
      // This sends commands and waits for responses
      this.fetchDeviceStatusAndDetail().catch(console.error)

      return true
    } catch (error) {
      clearStoredState()
      this.device = null
      this.emit('error', error instanceof Error ? error : new Error(String(error)))
      return false
    }
  }

  async disconnect(): Promise<void> {
    this.removeEventListeners()

    if (this.device && this.device.opened) {
      try {
        await this.device.close()
      } catch {
        // ignore
      }
    }

    this.device = null
    this.isDeviceConnected = false
    clearStoredState()
    this.emit('connection-stage', { stage: 'disconnected' })
    this.emit('device-disconnected', undefined)
  }

  /**
   * 发送原始数据到设备
   * 
   * @param data - 要发送的数据（Uint8Array 或十六进制字符串）
   * @param waitForResponse - 是否等待设备响应
   * @returns 如果 waitForResponse 为 true，返回设备响应；否则返回 undefined
   */
  async sendData(data: Uint8Array | string, waitForResponse = false): Promise<Uint8Array | void> {
    if (!this.device || !this.device.opened) {
      throw new Error('Device not connected')
    }

    // Check if already waiting for a response (prevent concurrent commands)
    if (this.isWaitingForResponse) {
      throw new Error('Another command is already waiting for response')
    }

    const dataArray =
      typeof data === 'string' ? new Uint8Array(data.split(' ').map(h => parseInt(h, 16))) : data

    const hexString = Array.from(dataArray).map(b => b.toString(16).padStart(2, '0').toUpperCase()).join(' ')
    console.log('[HID] Sending data:', hexString)

    await this.device.sendReport(49, dataArray)

    if (!waitForResponse) {
      console.log('[HID] Data sent (no wait)')
      return
    }

    // Single listener architecture: register to global state machine
    console.log('[HID] Registering for response...')
    this.isWaitingForResponse = true

    return new Promise((resolve, reject) => {
      this.commandTimeout = setTimeout(() => {
        this.clearCommandState()
        console.log('[HID] Response timeout')
        reject(new Error('Device response timeout'))
      }, 5000)

      this.commandResolver = resolve
      this.commandRejecter = reject
    })
  }

  private clearCommandState(): void {
    if (this.commandTimeout) {
      clearTimeout(this.commandTimeout)
      this.commandTimeout = null
    }
    this.commandResolver = null
    this.commandRejecter = null
    this.isWaitingForResponse = false
  }

  /**
   * 发送命令到设备并等待响应
   * 
   * @param command - 命令类型
   * @param data - 命令数据
   * @returns 设备状态对象或 null（如果解析失败）
   */
  async sendCommand(command: number, data: number[] = []): Promise<DeviceStatus | null> {
    try {
      const frame = buildCommand(command as any, data)
      const response = await this.sendData(frame, true)
      if (!response) return null

      const hexString = bytesToHexString(response)
      return parseHeadsetResponse(hexString)
    } catch (error) {
      this.emit('error', error instanceof Error ? error : new Error(String(error)))
      return null
    }
  }

  /**
   * Fetch device status and detail like original project fetchDeviceStatus()
   * Sends commands and waits for responses (serial, not parallel)
   */
  private async fetchDeviceStatusAndDetail(): Promise<void> {
    if (!this.device || !this.device.opened) {
      console.log('[HID] Cannot fetch status: device not open')
      return
    }

    try {
      // 1) 先拿设备详情，确保主界面可渲染正确机型素材
      console.log('[HID] Step 1: Getting device detail...')
      const frame1 = buildGetDeviceDetailCommand()
      const response1 = await this.sendData(frame1, true)
      if (response1) {
        const hexString = bytesToHexString(response1)
        const detail = parseDeviceDetailResponse(hexString)
        if (detail) {
          console.log('[HID] Device detail:', detail.deviceName)
          this.emit('device-connected', detail)
        }
      }

      // 2) 再拿状态帧，用 linkStatus/headsetConnected 最终确认业务连接
      console.log('[HID] Step 2: Getting device status...')
      const frame2 = buildGetStatusCommand()
      const response2 = await this.sendData(frame2, true)
      if (response2) {
        const hexString = bytesToHexString(response2)
        const status = parseHeadsetResponse(hexString)
        if (status) {
          console.log('[HID] Device status received, battery:', status.batteryLevel)
          this.emit('status-updated', status)
          // 只从 false -> true 提升一次，避免短时波动造成连接态抖动
          if (!this.isDeviceConnected) {
            const ok = this.checkDeviceConnectionStatus(status)
            if (ok) {
              console.log('[HID] Device connection confirmed!')
              this.isDeviceConnected = true
              const saved = loadStoredState()
              saveStoredState({ ...saved, isDeviceConnected: true })
              this.emit('connection-stage', { stage: 'connected', connectionType: this.getConnectionType() })
            }
          }
        }
      }
    } catch (error) {
      console.error('[HID] Failed to fetch device status/detail:', error)
    }
  }

  /**
   * 获取设备状态
   * 请求设备当前的各项状态参数
   * 
   * @returns 设备状态对象或 null
   */
  async getDeviceStatus(): Promise<DeviceStatus | null> {
    try {
      const frame = buildGetStatusCommand()
      const response = await this.sendData(frame, true)
      if (!response) return null

      const hexString = bytesToHexString(response)
      const status = parseHeadsetResponse(hexString)

      // confirm pairing (original onDeviceConnectedSuccessfully)
      if (status && !this.isDeviceConnected) {
        const ok = this.checkDeviceConnectionStatus(status)
        if (ok) {
          this.isDeviceConnected = true
          const saved = loadStoredState()
          saveStoredState({ ...saved, isDeviceConnected: true })
          this.emit('connection-stage', { stage: 'connected', connectionType: this.getConnectionType() })

          const detail = await this.getDeviceDetail()
          if (detail) {
            this.emit('device-connected', detail)
          }
        }
      }

      return status
    } catch (error) {
      this.emit('error', error instanceof Error ? error : new Error(String(error)))
      return null
    }
  }

  /**
   * 获取设备详情
   * 请求设备名称、版本等详细信息
   * 
   * @returns 设备详情对象或 null
   */
  async getDeviceDetail(): Promise<DeviceDetail | null> {
    try {
      const frame = buildGetDeviceDetailCommand()
      const response = await this.sendData(frame, true)
      if (!response) return null

      const hexString = bytesToHexString(response)
      return parseDeviceDetailResponse(hexString)
    } catch (error) {
      this.emit('error', error instanceof Error ? error : new Error(String(error)))
      return null
    }
  }

  isConnected(): boolean {
    return !!this.device && this.device.opened
  }

  /** 供固件升级模块获取底层 HID 设备 */
  getHidDevice(): HIDDevice | null {
    return this.device
  }

  getDeviceInfo(): DeviceInfo | null {
    if (!this.device) return null

    return {
      productId: this.device.productId,
      vendorId: this.device.vendorId,
      productName: this.device.productName,
      serialNumber: this.device.serialNumber,
      opened: this.device.opened,
    }
  }

  private setupEventListeners(): void {
    if (!this.device) return

    console.log('[HID] Setting up input report listener')

    this.inputReportHandler = (e: HIDInputReportEvent) => {
      if (e.reportId !== 49) return

      const value = new Uint8Array(e.data.buffer)
      const hexString = bytesToHexString(value)

      // 1) 命令-应答通道优先：同步请求先消费，避免被实时流量打断
      if (this.isWaitingForResponse && this.commandResolver) {
        console.log('[HID] Command response received:', hexString.substring(0, 30))
        this.commandResolver(value)
        this.clearCommandState()
        return // Don't process as real-time data
      }

      // 2) 其余数据作为实时数据广播给上层
      console.log('[HID] Real-time data:', hexString.substring(0, 50))
      this.emit('data-received', value)

      // 3) 实时帧也可兜底确认连接成功（防止首轮查询偶发超时）
      const status = parseHeadsetResponse(hexString)
      if (status) this.emit('status-updated', status)
      if (status && !this.isDeviceConnected) {
        const ok = this.checkDeviceConnectionStatus(status)
        if (ok) {
          console.log('[HID] Device connection confirmed!')
          this.isDeviceConnected = true
          const saved = loadStoredState()
          saveStoredState({ ...saved, isDeviceConnected: true })
          this.emit('connection-stage', { stage: 'connected', connectionType: this.getConnectionType() })
          this.getDeviceDetail().then(detail => {
            if (detail) this.emit('device-connected', detail)
          })
        }
      }
    }

    this.device.addEventListener('inputreport', this.inputReportHandler)
  }

  /** 只移除 inputreport 监听，不移除全局 HID 连接/断开监听 */
  private removeInputListener(): void {
    if (this.device && this.inputReportHandler) {
      this.device.removeEventListener('inputreport', this.inputReportHandler)
      this.inputReportHandler = null
    }
    this.clearCommandState()
  }

  /** 断开当前设备但保留全局 HID 监听（用于热切换） */
  private async disconnectCurrent(): Promise<void> {
    this.removeInputListener()
    if (this.device?.opened) {
      try { await this.device.close() } catch { /* ignore */ }
    }
    this.device = null
    this.isDeviceConnected = false
    clearStoredState()
    this.emit('device-disconnected', undefined)
  }

  private removeEventListeners(): void {
    this.removeInputListener()

    if (navigator.hid) {
      if (this.externalDisconnectHandler) {
        navigator.hid.removeEventListener('disconnect', this.externalDisconnectHandler)
      }
      if (this.externalConnectHandler) {
        navigator.hid.removeEventListener('connect', this.externalConnectHandler)
      }
    }
  }

  private setupGlobalHIDListeners(): void {
    if (!navigator.hid) return

    // avoid duplicates
    if (this.externalDisconnectHandler) {
      navigator.hid.removeEventListener('disconnect', this.externalDisconnectHandler)
    }
    if (this.externalConnectHandler) {
      navigator.hid.removeEventListener('connect', this.externalConnectHandler)
    }

    // 插入：记录最新插入设备 key，若当前无连接则自动连接；若已连接则切换到新设备
    this.externalConnectHandler = async (e: HIDConnectionEvent) => {
      const dev = e.device
      if (this.hasNormalModeReport49(dev)) {
        this.lastConnectedDeviceKey = `${dev.vendorId}:${dev.productId}`
      }
      if (this.device) {
        // 已有连接：先断开当前，再连新设备（取最新插入优先）
        await this.disconnectCurrent()
      }
      await this.autoConnect()
    }

    // 拔出：断开当前，尝试切换到其他已授权设备
    this.externalDisconnectHandler = async (e: HIDConnectionEvent) => {
      if (e.device !== this.device) return
      this.removeInputListener()
      this.device = null
      this.isDeviceConnected = false
      clearStoredState()
      this.emit('device-disconnected', undefined)

      // 尝试切换到剩余已授权设备
      const remaining = await this.getAuthorizedDevices()
      if (remaining.length > 0) {
        await this.autoConnect()
      } else {
        this.emit('connection-stage', { stage: 'disconnected', message: '设备已移除' })
      }
    }

    navigator.hid.addEventListener('connect', this.externalConnectHandler)
    navigator.hid.addEventListener('disconnect', this.externalDisconnectHandler)
  }
}

// Singleton instance
export const hidManager = new HIDDeviceManager()
